import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/feature/AdminLayout";
import { useAdminI18n } from "@/hooks/useAdminI18n";
import { supabase } from "@/lib/supabase";

/* ─── Collapsible Section ─── */
function CollapsibleSection({
  title,
  icon,
  children,
  defaultOpen = true,
}: {
  title: string;
  icon: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-lg border border-hj-gray-border overflow-hidden mb-4">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 md:px-5 py-3 md:py-4 border-b border-hj-gray-border cursor-pointer"
        aria-expanded={open}
      >
        <h2 className="text-base md:text-lg font-bold text-hj-dark flex items-center gap-2 font-heading">
          <i className={`${icon} text-hj-red`}></i>
          {title}
        </h2>
        <i className={`ri-arrow-down-s-line text-lg text-hj-gray transition-transform ${open ? "" : "-rotate-90"}`}></i>
      </button>
      {open && <div className="p-4 md:p-5">{children}</div>}
    </div>
  );
}

/* ─── Stat Card ─── */
function StatCard({
  label,
  value,
  sub,
  icon,
  color,
  loading,
}: {
  label: string;
  value: string | number;
  sub: string;
  icon: string;
  color: string;
  loading?: boolean;
}) {
  return (
    <div className={`bg-white rounded-lg p-3 md:p-4 border border-hj-gray-border text-left`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] md:text-xs text-hj-gray mb-1 font-body">{label}</p>
          {loading ? (
            <div className="h-6 w-12 bg-gray-100 rounded animate-pulse"></div>
          ) : (
            <p className="text-lg md:text-xl font-bold text-hj-dark truncate">{value}</p>
          )}
          <p className="text-[10px] text-hj-gray mt-0.5 font-body">{sub}</p>
        </div>
        <div className={`w-9 h-9 md:w-10 md:h-10 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
          <i className={`${icon} text-lg md:text-xl`}></i>
        </div>
      </div>
    </div>
  );
}

/* ─── Quick Action Button ─── */
function QuickAction({
  to,
  icon,
  label,
  color = "text-hj-red",
}: {
  to: string;
  icon: string;
  label: string;
  color?: string;
}) {
  return (
    <Link
      to={to}
      className="flex flex-col items-center gap-2 p-3 md:p-4 rounded-lg border border-hj-gray-border hover:border-hj-red hover:bg-hj-red/5 transition-colors text-center active:scale-[0.98] min-h-[80px] justify-center"
    >
      <i className={`${icon} text-xl md:text-2xl ${color}`}></i>
      <span className="text-xs font-medium text-hj-dark font-body">{label}</span>
    </Link>
  );
}

/* ─── Recent Article Row ─── */
function ArticleRow({
  article,
  index,
  editLabel,
}: {
  article: { id: string; title: string; category: string; published_at: string; status: string };
  index?: number;
  editLabel: string;
}) {
  return (
    <div className="flex items-center gap-3 py-3 hover:bg-gray-50 transition-colors -mx-4 px-4 md:-mx-5 md:px-5">
      {index !== undefined && (
        <span className="w-7 h-7 rounded-full bg-hj-gray-light flex items-center justify-center text-xs font-bold text-hj-gray shrink-0">
          {index + 1}
        </span>
      )}
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-medium text-hj-dark truncate font-body">{article.title}</h3>
        <div className="flex items-center gap-2 text-xs text-hj-gray mt-0.5 font-body flex-wrap">
          <span>{article.category || "—"}</span>
          <span>{article.published_at ? new Date(article.published_at).toLocaleDateString("de-CH") : "—"}</span>
        </div>
      </div>
      <Link
        to={`/admin/artikel-bearbeiten/${article.id}`}
        className="shrink-0 px-2.5 py-1.5 text-xs font-medium text-hj-red border border-hj-red rounded-md hover:bg-hj-red hover:text-white transition-colors whitespace-nowrap"
      >
        {editLabel}
      </Link>
    </div>
  );
}

/* ─── Stats type ─── */
interface DashboardStats {
  totalArticles: number;
  publishedArticles: number;
  draftArticles: number;
  scheduledArticles: number;
  totalCategories: number;
  totalAuthors: number;
  totalProfiles: number;
  totalMedia: number;
  totalSubscribers: number;
}

export default function AdminDashboard() {
  const { t } = useAdminI18n();
  const [stats, setStats] = useState<DashboardStats>({
    totalArticles: 0,
    publishedArticles: 0,
    draftArticles: 0,
    scheduledArticles: 0,
    totalCategories: 0,
    totalAuthors: 0,
    totalProfiles: 0,
    totalMedia: 0,
    totalSubscribers: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [recentArticles, setRecentArticles] = useState<
    { id: string; title: string; category: string; published_at: string; status: string }[]
  >([]);
  const [articlesLoading, setArticlesLoading] = useState(true);

  useEffect(() => {
    async function loadStats() {
      setStatsLoading(true);
      try {
        const [
          articlesRes,
          publishedRes,
          draftRes,
          scheduledRes,
          categoriesRes,
          authorsRes,
          profilesRes,
          mediaRes,
          subscribersRes,
        ] = await Promise.all([
          supabase.from("articles").select("id", { count: "exact", head: true }),
          supabase.from("articles").select("id", { count: "exact", head: true }).eq("status", "published"),
          supabase.from("articles").select("id", { count: "exact", head: true }).eq("status", "draft"),
          supabase.from("articles").select("id", { count: "exact", head: true }).eq("status", "scheduled"),
          supabase.from("categories").select("id", { count: "exact", head: true }),
          supabase.from("authors").select("id", { count: "exact", head: true }),
          supabase.from("profiles").select("id", { count: "exact", head: true }),
          supabase.from("media").select("id", { count: "exact", head: true }),
          supabase.from("newsletter_subscribers").select("id", { count: "exact", head: true }),
        ]);

        setStats({
          totalArticles: articlesRes.count ?? 0,
          publishedArticles: publishedRes.count ?? 0,
          draftArticles: draftRes.count ?? 0,
          scheduledArticles: scheduledRes.count ?? 0,
          totalCategories: categoriesRes.count ?? 0,
          totalAuthors: authorsRes.count ?? 0,
          totalProfiles: profilesRes.count ?? 0,
          totalMedia: mediaRes.count ?? 0,
          totalSubscribers: subscribersRes.count ?? 0,
        });
      } catch {
        // keep zeros on error
      }
      setStatsLoading(false);
    }

    async function loadRecentArticles() {
      setArticlesLoading(true);
      const { data } = await supabase
        .from("articles")
        .select("id, title, category, published_at, status")
        .order("published_at", { ascending: false })
        .limit(5);
      setRecentArticles(
        (data ?? []) as { id: string; title: string; category: string; published_at: string; status: string }[]
      );
      setArticlesLoading(false);
    }

    loadStats();
    loadRecentArticles();
  }, []);

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5 md:mb-6">
          <h1 className="text-xl md:text-2xl font-bold text-hj-dark font-heading">{t("dashboard.title")}</h1>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full whitespace-nowrap">
              <i className="ri-checkbox-circle-line mr-1"></i>
              {t("dashboard.adSenseReady")}
            </span>
          </div>
        </div>

        {/* Stats Grid */}
        <CollapsibleSection title={t("dashboard.overview")} icon="ri-dashboard-3-line" defaultOpen={true}>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard
              label={t("dashboard.stats.articles")}
              value={stats.totalArticles}
              sub={`${stats.publishedArticles} ${t("dashboard.stats.published")}`}
              icon="ri-article-line"
              color="bg-hj-red/10 text-hj-red"
              loading={statsLoading}
            />
            <StatCard
              label={t("dashboard.stats.drafts")}
              value={stats.draftArticles}
              sub={`${stats.scheduledArticles} ${t("dashboard.stats.scheduled")}`}
              icon="ri-draft-line"
              color="bg-amber-100 text-amber-600"
              loading={statsLoading}
            />
            <StatCard
              label={t("dashboard.stats.categories")}
              value={stats.totalCategories}
              sub={t("common.active")}
              icon="ri-folder-line"
              color="bg-sky-100 text-sky-600"
              loading={statsLoading}
            />
            <StatCard
              label={t("dashboard.stats.authors")}
              value={stats.totalAuthors}
              sub={t("dashboard.stats.regular")}
              icon="ri-user-line"
              color="bg-emerald-100 text-emerald-600"
              loading={statsLoading}
            />
            <StatCard
              label={t("dashboard.stats.users")}
              value={stats.totalProfiles}
              sub={t("dashboard.stats.registered")}
              icon="ri-team-line"
              color="bg-violet-100 text-violet-600"
              loading={statsLoading}
            />
            <StatCard
              label={t("dashboard.stats.media")}
              value={stats.totalMedia}
              sub={t("dashboard.stats.files")}
              icon="ri-image-line"
              color="bg-teal-100 text-teal-600"
              loading={statsLoading}
            />
            <StatCard
              label={t("dashboard.stats.newsletter")}
              value={stats.totalSubscribers}
              sub={t("dashboard.stats.subscribers")}
              icon="ri-mail-line"
              color="bg-pink-100 text-pink-600"
              loading={statsLoading}
            />
          </div>
        </CollapsibleSection>

        {/* Quick Actions */}
        <CollapsibleSection title={t("dashboard.quickActions")} icon="ri-flashlight-line" defaultOpen={false}>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            <QuickAction to="/admin/artikel-neu" icon="ri-add-circle-line" label={t("dashboard.quickAction.newArticle")} />
            <QuickAction to="/admin/breaking" icon="ri-flashlight-line" label={t("dashboard.quickAction.breaking")} color="text-amber-500" />
            <QuickAction to="/admin/mediathek" icon="ri-image-add-line" label={t("dashboard.quickAction.media")} color="text-sky-500" />
            <QuickAction to="/admin/seo" icon="ri-search-line" label={t("dashboard.quickAction.seo")} color="text-emerald-500" />
            <QuickAction to="/admin/newsletter" icon="ri-mail-send-line" label={t("dashboard.quickAction.newsletter")} color="text-pink-500" />
            <QuickAction to="/admin/analytik" icon="ri-bar-chart-2-line" label={t("dashboard.quickAction.analytics")} color="text-violet-500" />
          </div>
        </CollapsibleSection>

        {/* Latest Articles */}
        <CollapsibleSection title={t("dashboard.latestArticles")} icon="ri-time-line" defaultOpen={true}>
          {articlesLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-12 bg-gray-100 rounded animate-pulse"></div>
              ))}
            </div>
          ) : recentArticles.length === 0 ? (
            <div className="text-center py-8">
              <i className="ri-article-line text-3xl text-hj-gray mb-2 block"></i>
              <p className="text-sm text-hj-gray">{t("dashboard.noArticles")}</p>
              <Link
                to="/admin/artikel-neu"
                className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-hj-red text-white text-sm font-medium rounded-md hover:bg-hj-red-light transition-colors"
              >
                <i className="ri-add-line"></i>
                {t("dashboard.quickAction.newArticle")}
              </Link>
            </div>
          ) : (
            <>
              <div className="divide-y divide-hj-gray-border">
                {recentArticles.map((article, i) => (
                  <ArticleRow key={article.id} article={article} index={i} editLabel={t("dashboard.article.edit")} />
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-hj-gray-border text-center">
                <Link
                  to="/admin/artikel"
                  className="text-sm text-hj-red hover:text-hj-red-light transition-colors font-body"
                >
                  {t("dashboard.allArticles")} <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
            </>
          )}
        </CollapsibleSection>
      </div>
    </AdminLayout>
  );
}
